import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import { useTranslation } from "react-i18next";

export interface BaselineVitals {
  spo2: number;
  weight: number;
  bpSystolic: number;
  bpDiastolic: number;
  fev1: number;
  heartRate: number;
  bloodGlucose: number;
  pef: number;
  perfusionIndex: number;
}

interface BaselineVitalsDialogProps {
  open: boolean;
  onClose: () => void;
  initialVitals: BaselineVitals;
  onSave: (vitals: BaselineVitals) => void;
  includedMeasurements?: string[]; // Only show fields included in engagement plan
}

const BaselineVitalsDialog: React.FC<BaselineVitalsDialogProps> = ({
  open,
  initialVitals,
  onClose,
  onSave,
  includedMeasurements,
}) => {
  const { t } = useTranslation();
  const [spo2, setSpo2] = useState(initialVitals.spo2);
  const [weight, setWeight] = useState(initialVitals.weight);
  const [bpSystolic, setBpSystolic] = useState(initialVitals.bpSystolic);
  const [bpDiastolic, setBpDiastolic] = useState(initialVitals.bpDiastolic);
  const [fev1, setFev1] = useState(initialVitals.fev1);
  const [heartRate, setHeartRate] = useState(initialVitals.heartRate);
  const [bloodGlucose, setBloodGlucose] = useState(initialVitals.bloodGlucose);
  const [pef, setPef] = useState(initialVitals.pef);
  const [perfusionIndex, setPerfusionIndex] = useState(initialVitals.perfusionIndex);

  // Keep internal state in sync when initialVitals or open changes
  React.useEffect(() => {
    setSpo2(initialVitals.spo2);
    setWeight(initialVitals.weight);
    setBpSystolic(initialVitals.bpSystolic);
    setBpDiastolic(initialVitals.bpDiastolic);
    setFev1(initialVitals.fev1);
    setHeartRate(initialVitals.heartRate);
    setBloodGlucose(initialVitals.bloodGlucose);
    setPef(initialVitals.pef);
    setPerfusionIndex(initialVitals.perfusionIndex);
  }, [initialVitals, open]);

  // Normalize measurement type list for flexible matching
  const normalized = (includedMeasurements || []).map((s) => (s || "").toLowerCase());
  const hasType = (needle: string) => normalized.some((s) => s.includes(needle));

  // Decide which fields to show based on engagement plan
  const showSpo2 = includedMeasurements ? hasType("spo2") || hasType("oxygen") : true;
  const showWeight = includedMeasurements ? hasType("weight") : true;
  const showBP = includedMeasurements ? (hasType("blood") && hasType("pressure")) || hasType("bp") : true;
  const showFEV1 = includedMeasurements ? hasType("fev1") || hasType("peakflow") || hasType("peak") : true;
  const showPEF = includedMeasurements ? hasType("pef") || hasType("peakflow") || hasType("peak") : true;
  const showHR = includedMeasurements ? hasType("heart") || normalized.includes("hr") : true;
  const showGlucose = includedMeasurements ? hasType("glucose") : true;
  const showPerfusion = includedMeasurements ? showSpo2 || hasType("perfusion") || hasType("perf") : true;

  const handleSave = () => {
    onSave({ spo2, weight, bpSystolic, bpDiastolic, fev1, heartRate, bloodGlucose, pef, perfusionIndex });
    // Optionally clear fields after saving.
    /*
    setSpo2(0);
    setWeight(0);
    setBpSystolic(0);
    setBpDiastolic(0);
    setFev1(0);
    setHeartRate(0);
    setBloodGlucose(0);
    */
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("Register Baseline Vitals")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          {/* SpO2 group */}
          {(showSpo2 || showPerfusion) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("SpO₂") || 'SpO2'}</Typography>
              <Grid container spacing={2}>
                {showSpo2 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("SpO₂") || 'SpO2'}
                      value={spo2}
                      onChange={(e) => setSpo2(Number(e.target.value))}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                )}
                {showPerfusion && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("Perfusion Index") || 'Perfusion Index'}
                      value={perfusionIndex}
                      onChange={(e) => setPerfusionIndex(Number(e.target.value))}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Peak Flow group */}
          {(showFEV1 || showPEF) && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("Peak Flow") || 'Peak Flow'}</Typography>
              <Grid container spacing={2}>
                {showFEV1 && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("FEV1") || 'FEV1'}
                      value={fev1}
                      onChange={(e) => setFev1(Number(e.target.value))}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                )}
                {showPEF && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label={t("PEF") || 'PEF'}
                      value={pef}
                      onChange={(e) => setPef(Number(e.target.value))}
                      fullWidth
                      type="number"
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          {/* Blood Pressure group */}
          {showBP && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("Blood Pressure") || 'Blood Pressure'}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("Blood Pressure Systolic")}
                    value={bpSystolic}
                    onChange={(e) => setBpSystolic(Number(e.target.value))}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={t("Blood Pressure Diastolic")}
                    value={bpDiastolic}
                    onChange={(e) => setBpDiastolic(Number(e.target.value))}
                    fullWidth
                    type="number"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Single-parameter groups */}
          {showWeight && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("Weight")}</Typography>
              <TextField
                label={t("Weight")}
                value={weight}
                onChange={(e) => setWeight(Number(e.target.value))}
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
              />
            </Box>
          )}

          {showHR && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("Heart Rate")}</Typography>
              <TextField
                label={t("Heart Rate")}
                value={heartRate}
                onChange={(e) => setHeartRate(Number(e.target.value))}
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
              />
            </Box>
          )}

          {showGlucose && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{t("Blood Glucose")}</Typography>
              <TextField
                label={t("Blood Glucose")}
                value={bloodGlucose}
                onChange={(e) => setBloodGlucose(Number(e.target.value))}
                fullWidth
                type="number"
                inputProps={{ min: 0 }}
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("Cancel")}</Button>
        <Button variant="contained" onClick={handleSave} color="primary">
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BaselineVitalsDialog;
